<template>
  <div ref="wrapper" class="w-full relative" @focusout="onFocusOut" @keydown="onKeydown">
    <b-label v-if="label" :for="id">
      <span class="block text-sm text-white font-normal">{{ label }}</span>
    </b-label>

    <div
      class="h-[50px] relative focus-within:outline-2 focus-within:border-primary flex items-center w-full bg-transparent text-sm border border-[#373737] rounded-lg text-white"
    >
      <input
        :id="id"
        ref="inputRef"
        type="text"
        class="h-[50px] w-full bg-transparent rounded-lg focus:outline-none px-3 placeholder-gray-500 text-white"
        :placeholder="placeholder"
        :value="modelValue"
        autocomplete="off"
        :data-e2e="dataE2e"
        @input="onInput"
        @focus="onFocus"
      />
      <div
        v-if="isLoading"
        class="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 rounded-full border-2 border-gray-500 border-t-transparent animate-spin"
      />
    </div>

    <Teleport to="body">
      <transition
        enter-active-class="transition duration-100 ease-out"
        enter-from-class="transform scale-95 opacity-0"
        enter-to-class="transform scale-100 opacity-100"
        leave-active-class="transition duration-75 ease-out"
        leave-from-class="transform scale-100 opacity-100"
        leave-to-class="transform scale-95 opacity-0"
      >
        <ul
          v-if="isOpen && (suggestions.length > 0 || showNoResults)"
          class="fixed z-[100] max-h-60 overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-gray-300 ring-opacity-5"
          :style="dropdownStyle"
          role="listbox"
        >
          <li
            v-if="showNoResults"
            class="relative select-none py-2 px-4 text-gray-700 text-sm"
          >
            {{ t('location_autocomplete.no_results') }}
          </li>
          <li
            v-for="(item, index) in suggestions"
            :key="`${item.label}-${index}`"
            role="option"
            :aria-selected="index === highlightedIndex"
            :class="[
              'relative cursor-pointer select-none py-2 px-4',
              index === highlightedIndex ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
            ]"
            data-e2e="location-option"
            @mousedown.prevent="selectSuggestion(item)"
            @mouseenter="highlightedIndex = index"
          >
            <span class="block truncate text-sm font-medium">{{ item.label }}</span>
            <span class="block truncate text-xs text-gray-500">{{ item.context }}</span>
          </li>
        </ul>
      </transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useDebounceFn, useElementBounding } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import BLabel from '~/components/forms/BLabel.vue';
import {
  useLocationAutocomplete,
  type LocationSuggestion,
} from '~/composables/useLocationAutocomplete';

interface Props {
  modelValue: string;
  label?: string;
  placeholder?: string;
  id?: string;
  dataE2e?: string;
}

const props = withDefaults(defineProps<Props>(), {
  id: () => `location-${new Date().getTime().toString()}`,
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
}>();

const { t } = useI18n();
const { search } = useLocationAutocomplete();

const wrapper = ref<HTMLElement | null>(null);
const inputRef = ref<HTMLInputElement | null>(null);
const suggestions = ref<LocationSuggestion[]>([]);
const isOpen = ref(false);
const isLoading = ref(false);
const highlightedIndex = ref(-1);
const hasSearched = ref(false);

const { bottom, left, width } = useElementBounding(wrapper);
const dropdownStyle = computed(() => ({
  top: `${bottom.value + 4}px`,
  left: `${left.value}px`,
  width: `${width.value}px`,
}));

const showNoResults = computed(
  () => hasSearched.value && !isLoading.value && suggestions.value.length === 0,
);

const runSearch = useDebounceFn(async (query: string) => {
  isLoading.value = true;
  const results = await search(query);
  suggestions.value = results;
  highlightedIndex.value = -1;
  hasSearched.value = true;
  isLoading.value = false;
}, 300);

function onInput(event: Event) {
  const value = (event.target as HTMLInputElement).value;
  // Fallback texte libre : la saisie brute est toujours propagée (jamais bloquée).
  emit('update:modelValue', value);
  isOpen.value = true;
  if (value.trim().length < 2) {
    suggestions.value = [];
    hasSearched.value = false;
    isLoading.value = false;
    return;
  }
  runSearch(value);
}

function onFocus() {
  if (suggestions.value.length > 0) isOpen.value = true;
}

function selectSuggestion(item: LocationSuggestion) {
  emit('update:modelValue', item.label);
  isOpen.value = false;
  suggestions.value = [];
  hasSearched.value = false;
}

function onFocusOut(event: FocusEvent) {
  // Ferme uniquement quand le focus quitte tout le composant.
  // (Le clic sur une option utilise @mousedown.prevent → pas de perte de focus, donc pas de focusout.)
  const next = event.relatedTarget as Node | null;
  if (!next || !wrapper.value?.contains(next)) {
    isOpen.value = false;
  }
}

function onKeydown(event: KeyboardEvent) {
  if (!isOpen.value || suggestions.value.length === 0) return;
  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault();
      highlightedIndex.value = (highlightedIndex.value + 1) % suggestions.value.length;
      break;
    case 'ArrowUp':
      event.preventDefault();
      highlightedIndex.value =
        (highlightedIndex.value - 1 + suggestions.value.length) % suggestions.value.length;
      break;
    case 'Enter':
      if (highlightedIndex.value >= 0) {
        event.preventDefault();
        selectSuggestion(suggestions.value[highlightedIndex.value]);
      }
      break;
    case 'Escape':
      isOpen.value = false;
      break;
  }
}
</script>
